import { Avatar, Grid } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatHelsinkiDateTime } from '../../../common/dateHelper'
import { useStore } from '../../../Provider'
import { OPLoadingCircle } from '../../OPcomponents/OPLoadingcircle'
import { Data, OPNewTable } from '../../OPcomponents/OPNewTable'

interface ProfileOverviewProps {
  userId: string
}

const ProfileOverview = ({ userId }: ProfileOverviewProps): JSX.Element => {
  const { t, i18n } = useTranslation()
  const rootStore = useStore()
  const { personProfileOverview, loadingOverview, getUserProfileOverview } = rootStore.profileStore

  useEffect(() => {
    async function fetchProfileOverview() {
      if (Number(userId) !== personProfileOverview?.id) await getUserProfileOverview(Number(userId))
    }
    fetchProfileOverview()
  }, [userId, personProfileOverview?.id, rootStore.profileStore, getUserProfileOverview])

  const isEn = useMemo(() => i18n.language === 'en', [i18n.language])

  const displayedTitle = useMemo(() => {
    const eng = personProfileOverview?.titleEng?.trim()
    const fi = personProfileOverview?.titleFi?.trim()
    const candidates = isEn ? [eng, fi] : [fi, eng]
    return candidates.find((v) => v && v.length > 0) || ''
  }, [isEn, personProfileOverview?.titleEng, personProfileOverview?.titleFi])

  const rows: Data[] = useMemo(() => {
    const list: Data[] = []
    personProfileOverview?.languages?.forEach((language) =>
      list.push({
        id: language.languageId,
        rows: {
          [t('Language.Language')]: { text: language.languageName, width: '22%' },
          [t('Language.SkillLevel')]: { text: language.languageLevel, width: '17%' },
          [t('Language.Description')]: { text: language.description, width: '61%' },
        },
      }),
    )
    return list
  }, [personProfileOverview?.languages, t])

  const summaryText = useMemo(() => {
    const eng = personProfileOverview?.summaryEng?.trim()
    const fi = personProfileOverview?.summaryFi?.trim()
    if (isEn) return eng && eng.length > 0 ? eng : fi && fi.length > 0 ? fi : t('Profile.NoAddedSummary')
    return fi && fi.length > 0 ? fi : eng && eng.length > 0 ? eng : t('Profile.NoAddedSummary')
  }, [isEn, personProfileOverview?.summaryEng, personProfileOverview?.summaryFi, t])

  const workExpText = useMemo(() => {
    const eng = personProfileOverview?.workExperienceDescriptionEng?.trim()
    const fi = personProfileOverview?.workExperienceDescriptionFi?.trim()
    if (isEn) return eng && eng.length > 0 ? eng : fi && fi.length > 0 ? fi : t('Profile.NoAddedWorkExperience')
    return fi && fi.length > 0 ? fi : eng && eng.length > 0 ? eng : t('Profile.NoAddedWorkExperience')
  }, [isEn, personProfileOverview?.workExperienceDescriptionEng, personProfileOverview?.workExperienceDescriptionFi, t])

  const avatarSrc = useMemo(
    () => (personProfileOverview?.profilePhoto ? `data:image/jpeg;base64,${personProfileOverview.profilePhoto}` : undefined),
    [personProfileOverview?.profilePhoto],
  )

  const lastEditedFormatted = useMemo(() => {
    if (!personProfileOverview?.lastEdited) return null
    return formatHelsinkiDateTime(personProfileOverview.lastEdited, '')
  }, [personProfileOverview?.lastEdited])

  return (
    <Grid container spacing={5} className='gridcontainer--formcontainer__addnewpaddings'>
      {loadingOverview ? (
        <Grid item xs={12}>
          <OPLoadingCircle />
        </Grid>
      ) : (
        <>
          <Grid item xs={12}>
            <Grid container justifyContent={'space-between'} className='griditem--negativemargin'>
              <Grid item>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <p className='profile--titlecaption profpadding'>{t('Profile.ProfileTitle')}:</p>
                  </Grid>
                  <Grid item xs={12}>
                    <p className='profile--deptext profpadding'>{displayedTitle !== '' ? displayedTitle : t('Profile.NoAddedTitle')}</p>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Avatar className='profile--grid__profimage' src={avatarSrc} />
              </Grid>
            </Grid>
          </Grid>

          {/* Summary & Work Experience side by side on larger screens */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <p className='profile--titlecaption nopadding'>{t('Profile.ProfileSummary')}:</p>
              </Grid>
              <Grid item xs={12}>
                <p className='profile--deptext nopadding'>{summaryText}</p>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <p className='profile--titlecaption nopadding'>{t('Profile.ProfileWorkExperienceDescription')}:</p>
              </Grid>
              <Grid item xs={12}>
                <p className='profile--deptext nopadding'>{workExpText}</p>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={4}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <p className='profile--titlecaption nopadding'>{t('Profile.UserLastEditedLong')}:</p>
              </Grid>
              <Grid item xs={12}>
                <p className='profile--deptext nopadding' data-testid='lastEdited'>
                  {lastEditedFormatted || '-'}
                </p>
              </Grid>
            </Grid>
          </Grid>

          {/* Languages */}
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <p className='profile--titlecaption profpadding'>{t('Language.Header')}:</p>
              </Grid>
              <Grid item xs={12}>
                {rows.length > 0 ? (
                  <OPNewTable rows={rows} curLength={rows.length} />
                ) : (
                  <p className='profile--deptext profpadding'>{t('Profile.NoAddedLanguages')}</p>
                )}
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  )
}
export default observer(ProfileOverview)
